/* © 2014 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Aetonix, Sep 08, 2016
 * For information or permission request, email info@aetonixsystems.com
 */


var Model = require("../types/Model.js");
var xtend = require("xtend");
var UserMetricsUtils = require("../../utils/UserMetricsUtils");

var AETONIX_PERMISSIONS = [
	"manage",
	"personal",
	"urgencycontacts",
	"contacts",
	"device",
	"atouchaway",
	"reminders",
	"files",
	"forms",
	"metrics",
	"observations",
	"workflow",
	"groupforms",
	"resources",
	"devices"
];

var CAREPLAN_PERMISSIONS = [
	"careplan_Appointments",
	"careplan_CareTeam",
	"careplan_Client",
	"careplan_Goals",
	"careplan_HealthAssessments",
	"careplan_HealthConditions",
	"careplan_HospitalVisits",
	"careplan_OtherTreatments",
	"careplan_Situation",
	"careplan_Supports",
	"careplan_Treatments"
];

/**
 * Keeps track of the permissions granted any specified Manager for a User's data
 * @type {Model}
 */
module.exports = Model({
	name: "managedmanagerpermissions",
	init: init,
	uninit: uninit,
	listeners: listeners,
	triggers: triggers,
	_group: null,
	_who: null,
	_who_tracker: null,
	_manager: null,
	_manager_tracker: null,
	_groupforms: null,

	handle_view: handle_view,
	sayViewing: sayViewing,
	handle_permissionsUpdate: handle_permissionsUpdate,
	refresh: refresh,
	extendList: extendList,
	finish_save: finish_save,
	handle_change: handle_change,
	preparePermissions: preparePermissions
});

function init() {
	this._who = null;
	this._manager = null;
	this._groupforms = null;
	this.manager_tracker = this.dispatcher.createTracker();

	this.extendList({});
}

/**
 * Removes all listeners from the dispatcher
 */
function uninit() {
	this._who = null;
	this._manager = null;
	this._groupforms = null;
	this.manager_tracker.off();
	this.manager_tracker = null;
}

function listeners() {
	return {
		"connectivity:online": refresh,
		"managedgroupformlist:updated": handle_groupforms_updated
	};
}

/**
 * The model can be triggered to supply the permission for access to a given User's
 * data ie. when a manager clicks to open an information item, the item is
 * displayed or an error is displayed.
 */
function triggers() {
	return {
		view: handle_view,
		change: handle_change,
		reset: reset
	};
}

function reset() {
	this.emit("usermetrics:trigger:track", "close permissions for managed user's manager", {
		who: this._who,
		manager: this._manager
	});
	this._who = null;
	this._manager = null;
	this.extendList({});
}

function handle_view(data) {
	var user = data.user;
	var manager = data.manager;

	if (this._manager === manager  && this._user === user) {
		this.sayViewing();
		return;
	}

	var group = data.group;
	this._group = group;

	this._who = user;
	this._manager = manager;
	var tracker = this.manager_tracker;
	tracker.off();
	if(group)
		tracker.on("mqtt:managegroup/" + group + "/patient/u/" + user + "/manage/manager/permissions/updated", handle_permissionsUpdate.bind(this));
	else
		tracker.on("mqtt:u/" + user + "/manage/manager/permissions/updated", handle_permissionsUpdate.bind(this));

	this.refresh()
		.then(sayViewing.bind(this));

	this.emit("usermetrics:trigger:track", "open permissions for managed user's manager", {
		who: user,
		manager: manager
	});
}

function handle_permissionsUpdate(data) {
	var manager = data._id;
	if (!manager || manager !== this._manager) return;

	// Keep as long as file/forms are treated together as 'notes'
	if (!data.notes) {
		data["notes"] = data.files || data.forms;
	}
	this.extendList(data);
}

function sayViewing() {
	this.dispatch("viewing", {
		who: this._manager
	});
}

function refresh() {
	var user = this._who;
	var group = this._group;
	var manager = this._manager;
	if(!user || !manager) return;
	this.emit("save:trigger:start");
	return this.api.manage.manage.permissions.list(user, manager, group)
		.then(handle_notes.bind(this))
		.then(extendList.bind(this))
		.catch(console.error.bind(console))
		.then(finish_save.bind(this));
}

/**
 * Necessary until 'files' and 'forms' are treated separately as opposed
 * to being considered  jointly as 'notes'
 */
function handle_notes(data) {
	if(!data.notes) data["notes"] = data.file || data.forms;
	return data;
}

function handle_change(data) {
	const diff = UserMetricsUtils.difference(data, this.data);
	const update_fields = Object.keys(diff);
	var user = this._who;
	var group = this._group;
	var manager = this._manager;
	var permission = this.preparePermissions(data);
	this.emit("save:trigger:start");
	this.emit("usermetrics:trigger:track", "update managed user's manager permissions", {
		who: user,
		manager: manager,
		update_fields,
	});
	return this.api.manage.manage.permissions.update(user, manager, permission, group)
		.catch(console.error.bind(console))
		.then(finish_save.bind(this))
		.then(show_toast.bind(this));
}

function preparePermissions(data) {
	var permissions = data;
	var groupforms = this._groupforms;

	// Make sure all Aetonix permissions are 0 or 1, except for 'manage' which has
	// the same values as the careplan permissions ie. [0 .. 3]
	var aetonixchanges = AETONIX_PERMISSIONS.reduce(function (acc, permission) {
		var value = permissions[permission];

		// Convert 'notes' to 'files' & 'forms'
		if (permission === "files" || permission === "forms")
			value = permissions["notes"];

		// Convert boolean values
		if (typeof value === "boolean") {
			value ? value = 1 : value = 0;
		}
		// Convert boolean values entered as strings
		if (typeof value === "string" && permission !== "manage") {
			acc[permission] = (value === "true") ? 1 : 0;
		}
		//  Ensure non-manage permissions only have values 0 or 1
		//  while manage permissions are allowed values [0..3]
		if (typeof value !== "string" && (permission === "manage" || permission === "resources" || permission === "devices")) {
			acc[permission] = value;
		} else {
			acc[permission] = (value >= 1 || value) ? 1 : 0;
		}
		return acc;
	}, {});

	var groupformchanges = Object.keys(groupforms).reduce(function(acc, form){
		var value = permissions[form];
		if(value === undefined) return acc;
		if(typeof acc.groupforms !== "object") acc.groupforms = {};
		acc.groupforms[form] = value;
		return acc;
	}, aetonixchanges);

	// Make sure all Careplan permissions are [0..3]
	var finalchanges = CAREPLAN_PERMISSIONS.reduce(function (acc, permission) {
		var value = permissions[permission];
		if (typeof value === "number" && value <= 3) {
			acc[permission] = value;
		} else {
			acc[permission] = 0; // if "No access" or any other string or number
			if (value === "Read only" || value === "1") acc[permission] = 1;
			if (value === "Read/Propose change" || value === "2") acc[permission] = 2;
			if (value === "Read/propose/confirm" || value === "3") acc[permission] = 3;
		}
		return acc;
	}, groupformchanges);

	return finalchanges;
}

function finish_save() {
	this.emit("save:trigger:finish");
}

function extendList(data) {
	var groupforms = this._groupforms;
	var groupformsPermissions = data.groupforms;
	var newData;
	if(groupformsPermissions){
		newData = Object.keys(groupforms).reduce(function(acc, key){
			if(key === "access") return acc;
			acc[key] = groupformsPermissions[key];
			return acc;
		}, {});
	}
	this.extend(xtend(data, newData));
}

function show_toast(){
	this.emit("toast:trigger:show", {
		content: "managedprofile.saved"
	});
}

function handle_groupforms_updated(data){
	this._groupforms = data;
}
